import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import { Eye, EyeOff, Loader2, Mail } from 'lucide-react';
import { extractErrorMessage } from '../components/SafeError';
import AuthHeader from '../components/AuthHeader';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendSuccess('');
    setShowResend(false);
    setLoading(true);

    try {
      const user = await login(email, password);
      
      if (user.force_password_change) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMsg = typeof err.message === 'string' ? err.message : extractErrorMessage(err);
      setError(errorMsg);
      // Show resend button if error mentions verification
      if (errorMsg.toLowerCase().includes('verify')) {
        setShowResend(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    
    setResendLoading(true);
    setError('');
    setResendSuccess('');
    
    try {
      await authApi.resendVerification(email);
      setResendSuccess('Verification email sent! Please check your inbox.');
      setShowResend(false);
    } catch (err) {
      setError('Failed to send verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black/95" />
      
      <div className="relative w-full max-w-md" data-testid="login-page">
        <div className="card-panel p-8 shadow-card">
          <AuthHeader subtitle="Sign in to continue" />

          {/* Success message */}
          {resendSuccess && (
            <div className="mb-6 p-3 bg-green-900/30 border border-green-900/50 rounded-lg">
              <p className="text-sm text-green-300" data-testid="resend-success">{resendSuccess}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-900/50 rounded-lg">
              <p className="text-sm text-red-300" data-testid="login-error">{error}</p>
              {showResend && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 bg-glass-cyan/15 hover:bg-glass-cyan/25 text-glass-cyan rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="resend-verification-btn"
                >
                  {resendLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </button>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label-text block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="pilot@example.com"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="label-text block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-text-secondary hover:text-cockpit-text-primary"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-glass-cyan hover:opacity-80"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-cockpit-text-secondary">
              Don't have an account?{' '}
              <Link to="/register" className="text-glass-cyan hover:opacity-80 font-medium" data-testid="register-link">
                Register
              </Link>
            </p>
            <a
              href="https://flightsimacademy.co.uk"
              className="block mt-3 text-xs text-cockpit-text-secondary opacity-70 hover:opacity-100 transition-opacity"
              data-testid="back-to-website-link"
            >
              ← flightsimacademy.co.uk
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
